import { ClientsService } from './../../../shared/services/clients.service';
import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core';
import {
  FormArray,
  UntypedFormArray,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';

import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { UiService } from '@services/ui.service';
import { Client } from '@classes/client';
import { DictionaryValuesService } from '@services/dictionaryValues.service';
import { DictionaryValue } from '@classes/dictionary/dictionaryValue';
import { DictionariesService } from '@services/dictionaries.service';
import { Dictionary } from '@classes/dictionary/dictionary';
import { Site } from '@classes/site';
import { SitesService } from '@services/sites.service';

interface CivilityItem {
  id: number;
  name: string;
}

@Component({
  selector: 'app-transport-type-edit',
  templateUrl: './transport-type-edit.component.html',
  styleUrls: ['./transport-type-edit.component.scss'],
})
export class TransportTypeEditComponent
  extends FormEditBaseComponent
  implements OnInit
{
  @Input() dictionaryValue: DictionaryValue;
  @Input() transportTypes: DictionaryValue[];
  @Input() urgencyLevels: DictionaryValue[];
  dictionaries: Dictionary[];
  clients: Client[];

  sites: Site[] = [];
  selectableSites: Site[] = [];

  identifierPattern = '^[0-9]{0,50}$';

  stringMaxLength = 50;

  temperatureOptions: number[] = [-20, 5, 20];

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private dictionaryValuesSrv: DictionaryValuesService,
    private dictionariesSrv: DictionariesService,
    private clientsSrv: ClientsService,
    private _sitesSrv: SitesService,
    private cd: ChangeDetectorRef,
  ) {
    super(uiSrv, fb);
  }

  ngOnInit() {
    super.ngOnInit();
    this.initSub();
  }

  onAddSite(site?: Site): void {
    if (!site) {
      this.generateSelectableSites();
    }
    const control = this.fb.control(site || null);
    control.setValidators(Validators.required);
    this.getArray('sites').push(control);

    this.cd.detectChanges();
  }

  onRemoveSite(index: number): void {
    this.getArray('sites').removeAt(index);

    this.generateSelectableSites();
  }

  generateSelectableSites(): void {
    this.selectableSites = [...this.sites].filter(
      (worksite) =>
        !this.alreadyUsedSites.map((item) => item.id).includes(worksite.id),
    );
  }

  get sitesFormArray(): FormArray {
    return this.editForm.get('sites') as FormArray;
  }

  get alreadyUsedSites(): Site[] {
    return this.sitesFormArray.controls
      .map((control) => control.value)
      .filter((item) => item !== null);
  }

  initSub(): void {
    this.isLoading = true;

    const dictionariesPromise = new Promise((resolve, reject) => {
      this.dictionariesSrv.getList().subscribe((dictionariesFromSrv) => {
        this.dictionaries = dictionariesFromSrv;
        resolve(dictionariesFromSrv);
      });
    });

    const clientsPromise = new Promise((resolve, reject) => {
      this.clientsSrv.getList().subscribe((clientsFromSrv) => {
        this.clients = clientsFromSrv;
        resolve(clientsFromSrv);
      });
    });

    const sitesPromise = new Promise((resolve, reject) => {
      if (this.dictionaryValue && this.dictionaryValue.client) {
        this._sitesSrv
          .getListByClients([this.dictionaryValue.client.id])
          .subscribe((sites) => {
            this.sites = sites;
            resolve(sites);
          });
      } else {
        resolve([]);
      }
    });

    Promise.all([dictionariesPromise, clientsPromise, sitesPromise]).then(
      () => {
        this.initForm();
        this.isLoading = false;
      },
    );
  }

  onSubmit(): void {
    this.isLoading = true;
    if (this.dictionaryValue) {
      this.update();
    } else {
      this.create();
    }
  }

  onCancel(): void {
    this.editFinished.emit();
  }

  endForm(response: boolean) {
    if (response) {
      this.onSubmit();
    } else {
      this.onCancel();
    }
  }

  private update(): void {
    const dictionaryValueCopy: DictionaryValue = JSON.parse(
      JSON.stringify(this.dictionaryValue),
    );
    dictionaryValueCopy.name = this.editForm.value.name;
    dictionaryValueCopy.client = this.editForm.value.client;
    dictionaryValueCopy.dictionary = this.editForm.value.dictionary;
    dictionaryValueCopy.temperature = this.editForm.value.temperature !== undefined ? this.editForm.value.temperature : null;
    dictionaryValueCopy.sortOrder = this.dictionaryValue.sortOrder;
    dictionaryValueCopy.sites = this.editForm.value.sites;
    if (this.editForm.value.dictionary.code === 'transport-types') {
      this.dictionaryValuesSrv
        .putTransportType(dictionaryValueCopy)
        .subscribe(() => (this.isLoading = false));
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      this.dictionaryValuesSrv
        .putUrgencyLevel(dictionaryValueCopy)
        .subscribe(() => (this.isLoading = false));
    }
  }

  private create(): void {
    let sortOrder = 0;

    if (this.editForm.value.dictionary.code === 'transport-types') {
      sortOrder = this.transportTypes.length + 1;
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      sortOrder = this.urgencyLevels.length + 1;
    }

    this.dictionaryValue = new DictionaryValue(
      this.editForm.value.name,
      this.editForm.value.dictionary,
      this.editForm.value.client,
      sortOrder,
    );
    this.dictionaryValue.sites = this.editForm.value.sites;
    this.dictionaryValue.temperature = this.editForm.value.temperature !== undefined ? this.editForm.value.temperature : null;

    if (this.editForm.value.dictionary.code === 'transport-types') {
      this.dictionaryValuesSrv
        .postTransportType(this.dictionaryValue)
        .subscribe(
          () => (this.isLoading = false),
          () => (this.dictionaryValue = null),
        );
    } else if (this.editForm.value.dictionary.code === 'urgency-levels') {
      this.dictionaryValuesSrv.postUrgencyLevel(this.dictionaryValue).subscribe(
        () => (this.isLoading = false),
        () => (this.dictionaryValue = null),
      );
    }
  }

  private initForm(): void {
    let name = '';
    let client = null;
    let dictionary = null;
    let sites = null;

    if (this.dictionaryValue) {
      name = this.dictionaryValue.name;
      dictionary = this.dictionaryValue.dictionary;
      client = this.dictionaryValue.client || null;
      sites = this.dictionaryValue.sites;
    }

    this.editForm = this.fb.group({
      name: [
        name,
        [Validators.required, Validators.maxLength(this.stringMaxLength)],
      ],
      dictionary: [dictionary, [Validators.required]],
      client: [client, Validators.required],
      temperature: [this.dictionaryValue && this.dictionaryValue.temperature !== undefined ? this.dictionaryValue.temperature : null],
      sites: this.fb.array([]),
    });

    if (sites && sites.length) {
      sites.map((s) => this.onAddSite(s));
    }
  }

  public onClientChange(): void {
    const client = this.editForm.get('client').value;
    this.sites = [];
    this.getArray('sites').clear();
    if (client) {
      this._sitesSrv.getListByClients([client.id]).subscribe((sites) => {
        this.sites = sites;
      });
    }
  }
}
